<template>
    <div class="toolbar-wrap">
        <div>
            <p class="field-title" style="margin-left: 0.5rem">
                Company Addresses
                <Tooltip description="Manage your company's office locations." />
            </p>
        </div>
        <Button :disabled="isChannel || disableEditForDifferentOrigin" label="Add Address" class="mr-2 inline-block add-new-btn" @click="openNew" />
    </div>
    <div class="table-wrap">
        <table class="address-table" v-if="locationInfos?.length">
            <thead>
                <tr>
                    <th>Actions</th>
                    <th>Location Name</th>
                    <th>Address 1</th>
                    <th>Address 2</th>
                    <th>City</th>
                    <th>State/Province/Region</th>
                    <th>Postal Code</th>
                    <th>Country</th>
                    <th>Headquarters</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(location, index) in locationInfos" :key="index">
                    <td class="actions-column">
                        <div class="actions-column-inner">
                            <Button :disabled="isChannel || disableEditForDifferentOrigin" icon="pi pi-pencil" class="p-button-rounded p-button-warning mr-2"
                            @click="editLocation(location, index)" />
                            <Button :disabled="isChannel || disableEditForDifferentOrigin " icon="pi pi-trash" class="p-button-rounded p-button-warning"
                                @click="deleteLocation(location, index)" />
                        </div>
                    </td>
                    <td>{{ location.name }}</td>
                    <td>{{ location.address1 }}</td>
                    <td>{{ location.address2 }}</td>
                    <td>{{ location.city }}</td>
                    <td>{{ location.region }}</td>
                    <td>{{ location.postalCode }}</td>
                    <td>{{ location.country }}</td>
                    <td class="hq-column">
                        <i :class="['pi', location.primary ? 'pi-check' : 'pi-times']"></i>
                    </td>
                </tr>
            </tbody>
        </table>

        <Dialog :draggable="false" v-model:visible="locationDialog" :style="{ width: '600px' }" header="Location Details" :modal="true"
            class="p-fluid">
            <div class="field">
                <label for="name">Location Name</label>
                <InputText id="name" v-model="locationInfo.name" required="true" />
            </div>

            <div class="field">
                <label for="address1">Address 1</label>
                <Textarea id="address1" v-model="locationInfo.address1" required="true" rows="3" cols="20"
                    :autoResize="true" />
            </div>

            <div class="field">
                <label for="address2">Address 2</label>
                <Textarea id="address2" v-model="locationInfo.address2" rows="3" cols="20"
                    :autoResize="true" />
            </div>

            <div class="field">
                <label for="city">City</label>
                <InputText id="city" v-model="locationInfo.city" required="true" />
            </div>

            <div class="field">
                <label for="region">State/Province/Region</label>
                <InputText id="region" v-model="locationInfo.region" required="true" />
            </div>

            <div class="field">
                <label for="postalCode">Postal Code</label>
                <InputText id="postalCode" v-model="locationInfo.postalCode" required="true" />
            </div>

            <div class="field">
                <label for="country">Country</label>
                <TreeSelect v-model="locationInfo.countryName" :options="countryListOptions"
                    placeholder="Select country" selectionMode="single" panelClass="p-tree-single" class="country-select" />
            </div>

            <div class="field-checkbox">
                <Checkbox id="primary" v-model="locationInfo.primary" :binary="true" />
                <label for="primary" style="margin-left: 1rem">Set as Headquarters</label>
            </div>

            <template #footer>
                <Button label="Cancel" class="p-button p-component p-button-outlined button" @click="hideDialog" />
                <Button v-if="editIndex === null" label="Add" class="p-button p-component button"
                    @click="onLocationInfoSave" />
                <Button v-else label="Update" class="p-button p-component button" @click="onLocationInfoSave" />
            </template>
        </Dialog>

        <Dialog :draggable="false" v-model:visible="deleteLocationDialog" :style="{ width: '450px' }" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle mr-3" style="font-size: 1.75rem; margin-right: 0.5rem" />
                <span v-if="locationInfo">Are you sure you want to delete <b>{{ locationInfo.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" class="p-button p-component p-button-outlined button"
                    @click="deleteLocationDialog = false" />
                <Button label="Yes" class="p-button p-component button" @click="onConfirmDeleteLocation" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import Tooltip from '../../components/Tooltip.vue';
import CountryService from '../../service/CountryService';
import { onMounted } from '@vue/runtime-core';
import { convertCountryDataToTreeSelect } from '../../service/common';

export default {
    name: 'LocationInfo',
    props: ['saveInd', 'locationInfoValues', 'isChannel', "disableEditForDifferentOrigin"],
    components: {
        Tooltip,
    },
    data() {
        return {
            locationInfos: [],
            locationDialog: false,
            deleteLocationDialog: false,
            locationInfo: {},
            submitted: false,
            editIndex: null,
            deleteLocationInfoIndex: null,
            intializeOnce: true,
            paginationData: { columnOffset: 0 },
            countryListOptions: [],
        };
    },
    setup() {
        const v = useVuelidate({ $stopPropagation: true });
        return { v };
    },
    created() {
        this.countryService = new CountryService();
        onMounted(() => {
            this.countryService.getCountries().then((data) => {
                this.countryCodeMapping = {};
                this.countryListOptions = convertCountryDataToTreeSelect(data);
            });
        });
    },
    watch: {
        v$: function () {
            if (this.v$.$dirty) {
                this.validationCall();
            }
        },
        saveInd() {
            if (this.saveInd) {
                this.validationCall();
            }
        },
        locationInfoValues() {
            try {
                let locationInfos = this.locationInfoValues.map((x) => {
                    return { ...x, countryName: { [x.country]: true } };
                });
                this.locationInfos = locationInfos || [];
            } catch (err) {
                console.error('Error', err.stack);
            }
        },
    },
    methods: {
        checkAndSetPrimeLocationAvailablity() {
            if (this.locationInfos?.find((location) => location?.primary)) {
                const primeLocationIndex = this.locationInfos?.findIndex((location) => location?.primary);
                this.locationInfos[primeLocationIndex].primary = false;

                return true;
            }
            return false;
        },
        onPage(event) {
            this.paginationData = {
                page: event?.page, // current page
                pageCount: event?.pageCount, // total page count
                rows: event?.rows, // Number of rows to display in new page
                columnOffset: event?.page * event?.rows,
            };
        },
        openNew() {
            this.locationInfo = {};
            this.submitted = false;
            this.locationDialog = true;
        },
        hideDialog() {
            this.locationDialog = false;
            this.submitted = false;
            this.editIndex = null;
        },
        editLocation(locationInfo, index) {
            this.locationInfo = { ...locationInfo };
            this.editIndex = this.paginationData?.columnOffset + index;
            this.locationDialog = true;
        },
        saveProduct() {
            this.submitted = true;
            this.locationDialog = false;
            this.locationInfo = {};
        },
        deleteLocation(locationInfo, index) {
            this.locationInfo = { ...locationInfo };
            this.deleteLocationInfoIndex = this.paginationData?.columnOffset + index;
            this.deleteLocationDialog = true;
        },
        onConfirmDeleteLocation() {
            this.locationInfos.splice(this.deleteLocationInfoIndex, 1);
            this.deleteLocationDialog = false;
            this.locationInfo = {};
            this.validationCall();
        },
        onLocationInfoSave() {
            if (!this.locationInfo.name || this.locationInfo.name.trim() === '') {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Error while creating data',
                    detail: 'Please enter name',
                    life: 3000,
                });
            }

            if (this.locationInfo?.primary) this.checkAndSetPrimeLocationAvailablity();

            if (this.locationInfo.name) this.locationInfo.name = this.locationInfo.name.trim();
            if (this.locationInfo.address1) this.locationInfo.address1 = this.locationInfo.address1.trim();
            if (this.locationInfo.address2) this.locationInfo.address2 = this.locationInfo.address2.trim();
            if (this.locationInfo.city) this.locationInfo.city = this.locationInfo.city.trim();
            if (this.locationInfo.region) this.locationInfo.region = this.locationInfo.region.trim();
            if (this.locationInfo.postalCode) this.locationInfo.postalCode = this.locationInfo.postalCode.trim();
            if (this.locationInfo.countryName) {
                let keys = Object.keys(this.locationInfo.countryName);
                if (keys.length) this.locationInfo.country = keys[0];
                else this.locationInfo.country = '';
            } else {
                this.locationInfo.country = '';
            }

            if (this.editIndex === null) {
                this.locationInfos = [...this.locationInfos, this.locationInfo];
            } else {
                this.locationInfos.splice(this.editIndex, 1, this.locationInfo);
                this.editIndex = null;
            }
            this.hideDialog();
            this.validationCall();
        },
        validationCall() {
            this.$emit('submit', this.v.$silentErrors.length, this.locationInfos);
            this.v.$touch();
        },
    },
};
</script>

<style scoped lang="scss">
::v-deep(.p-multiselect) {
    margin: 0.75rem 0;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    margin-bottom: 2rem;
}

::v-deep(.p-inputtext) {
    margin: 0.75rem 0;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
}

.add-new-btn {
    margin-top: 1.6rem !important;
    width: 8rem !important;
    background: #00796b !important;
    border: 2px solid #00796b !important;
    display: block;
    float: right !important;
}

::v-deep(.p-button.p-button-outlined) {
    background-color: transparent !important;
    color: #00796b !important;
    border: 1px solid;
}

::v-deep(.p-toolbar) {
    flex-wrap: wrap;
    background-color: #d6d6d6;
    height: 3rem;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded) {
    border-radius: 50%;
    height: 2.5rem;
    width: 2.5rem !important;
    margin: 0.5rem;
    background: #ffffff !important;
    border: none !important;
    display: flex;
    float: left !important;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded):hover {
    background: #e4edd9 !important;
}

::v-deep(.p-datatable) {
    .p-datatable-thead>tr>th {
        color: #707683 !important;
        background: #fbfbfb !important;
        transition: none;
    }
}

::v-deep(.country-select) {
    margin-left: 0;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
}

.field {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    margin-bottom: 0.75rem;
}

.field-title {
    margin-left: 1rem;
    font-size: 1.2rem;
    font-weight: 400;
    max-width: 30rem;
}

.confirmation-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.table-wrap {
    margin-top: 6rem;
    margin-bottom: 4rem;
    overflow-x: scroll;
}

.toolbar-wrap {
    width: 100%;
    background: #e4edd9;
    align-self: center;
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-top: 2rem;
}

.error-msg {
    margin-left: 1.25rem;
    color: red;
    font-size: 1rem;
    font-weight: 400;
}


//--------------------------------
.address-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.address-table th,
.address-table td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #eee;
    max-width: 200px;
    word-wrap: break-word;
    white-space: normal;
}

.address-table th {
    background: #fbfbfb;
    color: #707683;
    font-weight: 500;
}

.address-table tr:hover {
    background-color: #f8f9fa;
}

.actions-column-inner {
    display: flex;
}

.hq-column {
    text-align: center;
}

.hq-column i {
    font-size: 1.1rem;
}

.pi-check {
    color: #00796b;
}

.pi-times {
    color: #707683;
}

</style>
